import { Box, Group, Stack, TextInput, useMantineTheme } from '@mantine/core';
import { Icon, Text } from '@repo/foundations';
import { isEmpty } from 'lodash-es';
import { useCallback, useContext, useEffect } from 'react';
import { EMAIL_REGEX } from '../../constants';
import { AuthButton } from './AuthLayouts/AuthButton';
import { AuthStepStartContext } from './AuthStepStartContext';

interface AuthStepEmailComponentProps {
	cta: string;
}

export function AuthStepEmailComponent({ cta }: AuthStepEmailComponentProps) {
	const theme = useMantineTheme();

	const context = useContext(AuthStepStartContext);
	if (!context) {
		throw new Error(
			'AuthStepEmailComponent must be used within a AuthStepStartContextProvider'
		);
	}
	const { startEmailAuth, forceSSO, email, setEmail, error, setError } =
		context;

	const validateAndSendEmail = useCallback(
		(emailToValidate: string) => {
			setError(null);
			if (
				emailToValidate &&
				!isEmpty(emailToValidate) &&
				emailToValidate.match(EMAIL_REGEX)
			) {
				setEmail(emailToValidate);
				startEmailAuth();
				return true;
			} else {
				setError('Enter a valid email address');
				return false;
			}
		},
		[setEmail, setError, startEmailAuth]
	);

	const handleClick = useCallback(() => {
		validateAndSendEmail(email);
	}, [email, validateAndSendEmail]);

	// eslint-disable-next-line no-use-effect/no-use-effect
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const emailParam = params.get('email');

		if (!email && emailParam) {
			validateAndSendEmail(emailParam);
		}
	}, [email, validateAndSendEmail]);

	return (
		<Stack spacing="sm">
			{!forceSSO && (
				<>
					<TextInput
						autoFocus
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								handleClick();
							}
						}}
						onChange={(e) => {
							setError(null);
							setEmail(e.currentTarget.value);
						}}
						value={email}
						type="email"
						label="Work email"
						error={
							error && (
								<Group spacing="2xs">
									<Icon name="alertCircle" color="text/critical/default" />
									<Text
										weight="semibold"
										size="xs"
										color="text/critical/default"
									>
										{error}
									</Text>
								</Group>
							)
						}
						styles={{
							input: {
								borderRadius: theme.other.space[2],
								height: 44,
								backgroundColor:
									error && theme.other.getColor('surface/critical/default'),
							},
							label: { lineHeight: theme.other.typography.lineHeight.text.sm },
						}}
					/>
					<AuthButton onClick={handleClick} variant="primary">
						<Group align="center" position="center">
							<Box>{cta}</Box>
						</Group>
					</AuthButton>
				</>
			)}
		</Stack>
	);
}
